import { z } from 'zod';

// JS zod mirror of backend/src/contracts/supplierFactorRevision.ts and
// supplierFactorPropagation.ts. Kept byte-for-byte faithful to the backend
// schemas so the FE parses every response through the same shape the backend
// serialises. Any drift is a contract break — the contract test round-trips the
// backend fixtures against these schemas to catch it.

export const SupplierFactorPublicationState = Object.freeze({
  Published: 'published',
  Withdrawn: 'withdrawn',
});

export const SupplierFactorOrigin = Object.freeze({
  Direct: 'direct',
  Template: 'template',
});

export const SupplierFactorPropagationState = Object.freeze({
  Queued: 'queued',
  Processing: 'processing',
  Complete: 'complete',
  Degraded: 'degraded',
});

export const SupplierFactorPropagationEventKind = Object.freeze({
  Published: 'published',
  Corrected: 'corrected',
  Withdrawn: 'withdrawn',
});

export const supplierFactorObjectIdV1Schema = z.string().regex(/^[a-f\d]{24}$/i);
export const supplierFactorNonNegativeNumberV1Schema = z.number().finite().nonnegative();

/* -------------------------------------------------------------- propagation */

const propagationState = z.nativeEnum(SupplierFactorPropagationState);

export const supplierFactorPropagationEventV1Schema = z
  .object({
    version: z.literal(1),
    id: supplierFactorObjectIdV1Schema,
    eventKind: z.nativeEnum(SupplierFactorPropagationEventKind),
    seriesKey: z
      .object({
        operatorId: supplierFactorObjectIdV1Schema,
        installationId: supplierFactorObjectIdV1Schema,
        cnCode: z.string().min(1),
        productVariant: z.string(),
        productionMethodCodes: z.array(z.string()),
        methodFingerprint: z.string(),
      })
      .strict(),
    interval: z
      .object({ startInclusive: z.string().datetime(), endExclusive: z.string().datetime() })
      .strict(),
    revisionNumber: z.number().int().positive(),
    revisionId: supplierFactorObjectIdV1Schema,
    emissionsFactorId: supplierFactorObjectIdV1Schema,
    actor: z.object({ userId: z.string().min(1), role: z.string().min(1) }).strict(),
    targets: z.array(
      z
        .object({
          connectionId: supplierFactorObjectIdV1Schema,
          declarantId: supplierFactorObjectIdV1Schema,
          supplierId: supplierFactorObjectIdV1Schema,
          epoch: z.number().int().positive(),
        })
        .strict(),
    ),
  })
  .strict();

const supplierFactorPropagationHandlerStateV1Schema = z
  .object({
    state: propagationState,
    attempts: z.number().int().nonnegative(),
    appliedCount: z.number().int().nonnegative(),
    noOpCount: z.number().int().nonnegative(),
    lastError: z.string().optional(),
  })
  .strict();

export const supplierFactorPropagationV1Schema = z
  .object({
    available: z.boolean(),
    id: supplierFactorObjectIdV1Schema.nullable(),
    eventKind: z.nativeEnum(SupplierFactorPropagationEventKind).nullable(),
    state: propagationState,
    attempts: z.number().int().nonnegative(),
    retryable: z.boolean(),
    handlers: z
      .object({
        shipmentSnapshots: supplierFactorPropagationHandlerStateV1Schema,
        lots: supplierFactorPropagationHandlerStateV1Schema,
        cbamProducts: supplierFactorPropagationHandlerStateV1Schema,
        diagramPrecursors: supplierFactorPropagationHandlerStateV1Schema,
        dataRequestFulfilment: supplierFactorPropagationHandlerStateV1Schema,
      })
      .strict(),
    updatedAt: z.string().datetime(),
  })
  .strict();

/* ---------------------------------------------------------------- revisions */

export const supplierFactorPublicationStateV1Schema = z.nativeEnum(SupplierFactorPublicationState);

export const supplierFactorSeriesKeyV1Schema = z
  .object({
    operatorId: supplierFactorObjectIdV1Schema,
    installationId: supplierFactorObjectIdV1Schema,
    cnCode: z.string().min(1),
    productVariant: z.string(),
    productionMethodCodes: z.array(z.string().min(1)),
    methodFingerprint: z.string(),
  })
  .strict();

export const supplierFactorSeriesKeyRequestV1Schema = supplierFactorSeriesKeyV1Schema
  .omit({ operatorId: true, methodFingerprint: true })
  .extend({
    productVariant: z.string().optional(),
    productionMethodCodes: z.array(z.string().min(1)).optional(),
  })
  .strict();

export const supplierFactorIntervalV1Schema = z
  .object({
    startInclusive: z.string().datetime(),
    endExclusive: z.string().datetime(),
  })
  .strict();

export const supplierFactorEmissionsV1Schema = z
  .object({
    direct: supplierFactorNonNegativeNumberV1Schema,
    indirect: supplierFactorNonNegativeNumberV1Schema,
    total: supplierFactorNonNegativeNumberV1Schema,
  })
  .strict();

export const supplierFactorSefaRequestV1Schema = z
  .object({
    total: supplierFactorNonNegativeNumberV1Schema,
    benchmark: supplierFactorNonNegativeNumberV1Schema.optional(),
    benchmarkSource: z.string().min(1).optional(),
    production: supplierFactorNonNegativeNumberV1Schema.optional(),
    precursor: supplierFactorNonNegativeNumberV1Schema.optional(),
    cbamFactor: supplierFactorNonNegativeNumberV1Schema.max(100),
    reportingYear: z.number().int().min(2000).max(2200),
  })
  .strict();

export const supplierFactorCorrectionSefaRequestV1Schema = supplierFactorSefaRequestV1Schema
  .omit({ production: true, precursor: true })
  .strict();

export const supplierFactorAuthoritativeSefaV1Schema = z
  .object({
    total: supplierFactorNonNegativeNumberV1Schema,
    benchmark: supplierFactorNonNegativeNumberV1Schema.optional(),
    benchmarkSource: z.string().optional(),
    production: supplierFactorNonNegativeNumberV1Schema.nullable(),
    precursor: supplierFactorNonNegativeNumberV1Schema.nullable(),
  })
  .strict();

export const supplierFactorSourceSefaEvidenceV1Schema = z
  .object({
    production: supplierFactorNonNegativeNumberV1Schema.optional(),
    precursor: supplierFactorNonNegativeNumberV1Schema.optional(),
    total: supplierFactorNonNegativeNumberV1Schema.optional(),
    isAuthoritative: z.literal(false),
  })
  .strict();

// S15 safe-deploy mirror of the S14 backend `supplierFactorIndirectDetailV1Schema`.
// Accept-and-ignore for now (S14 deploys before its data is consumed) — S16 wires
// this into the review form + payloads. Faithful to the backend all-optional shape.
export const supplierFactorIndirectDetailV1Schema = z
  .object({
    electricityConsumed: supplierFactorNonNegativeNumberV1Schema.optional(),
    electricitySource: z.enum(['SOE01', 'SOE02', 'SOE03']).optional(),
    indirectEmissionFactor: supplierFactorNonNegativeNumberV1Schema.optional(),
    indirectEmissionFactorSource: z.enum(['01', '02']).optional(),
    indirectEmissionFactorSourceValue: z.string().trim().min(1).optional(),
    otherSourceIndication: z.string().trim().min(1).optional(),
    determinationType: z.enum(['01', '02', '03']).optional(),
    shareDefaultEmissions: supplierFactorNonNegativeNumberV1Schema.max(100).optional(),
  })
  .strict();

export const supplierFactorRevisionV1Schema = z
  .object({
    version: z.literal(1),
    id: supplierFactorObjectIdV1Schema,
    seriesKey: supplierFactorSeriesKeyV1Schema,
    interval: supplierFactorIntervalV1Schema,
    emissionsFactorId: supplierFactorObjectIdV1Schema,
    revisionNumber: z.number().int().positive(),
    publicationState: supplierFactorPublicationStateV1Schema,
    isCurrent: z.boolean(),
    withdrawnAt: z.string().datetime().optional(),
    supersedesRevisionId: supplierFactorObjectIdV1Schema.optional(),
    emissions: supplierFactorEmissionsV1Schema,
    authoritativeSefa: supplierFactorAuthoritativeSefaV1Schema,
    sourceSefaEvidence: supplierFactorSourceSefaEvidenceV1Schema.optional(),
    indirectDetail: supplierFactorIndirectDetailV1Schema.optional(),
    cbamFactor: supplierFactorNonNegativeNumberV1Schema.max(100),
    reportingYear: z.number().int().min(2000).max(2200),
    origin: z.nativeEnum(SupplierFactorOrigin),
    sourceDiagramId: supplierFactorObjectIdV1Schema.optional(),
    actor: z
      .object({
        userId: z.string().min(1),
        role: z.string().min(1),
      })
      .strict(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();

export const publishSupplierFactorRequestV1Schema = z
  .object({
    version: z.literal(1),
    seriesKey: supplierFactorSeriesKeyRequestV1Schema,
    interval: supplierFactorIntervalV1Schema,
    emissions: supplierFactorEmissionsV1Schema,
    sefa: supplierFactorCorrectionSefaRequestV1Schema,
    indirectDetail: supplierFactorIndirectDetailV1Schema.optional(),
    origin: z.literal(SupplierFactorOrigin.Direct),
  })
  .strict();

export const correctSupplierFactorRequestV1Schema = z
  .object({
    version: z.literal(1),
    currentRevisionId: supplierFactorObjectIdV1Schema,
    seriesKey: supplierFactorSeriesKeyRequestV1Schema.optional(),
    interval: supplierFactorIntervalV1Schema.optional(),
    emissions: supplierFactorEmissionsV1Schema.optional(),
    sefa: supplierFactorCorrectionSefaRequestV1Schema.optional(),
    indirectDetail: supplierFactorIndirectDetailV1Schema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (!value.emissions && !value.sefa && !value.indirectDetail) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A correction must include emissions, SEFA, or indirect detail',
      });
    }
  });

export const publishSupplierFactorResponseV1Schema = z
  .object({
    version: z.literal(1),
    status: z.literal(SupplierFactorPublicationState.Published),
    revision: supplierFactorRevisionV1Schema,
    propagation: supplierFactorPropagationV1Schema,
  })
  .strict();

export const correctSupplierFactorResponseV1Schema = publishSupplierFactorResponseV1Schema;

export const withdrawSupplierFactorRequestV1Schema = z
  .object({
    version: z.literal(1),
  })
  .strict();

export const withdrawSupplierFactorResponseV1Schema = z
  .object({
    version: z.literal(1),
    status: z.literal(SupplierFactorPublicationState.Withdrawn),
    revision: supplierFactorRevisionV1Schema,
    propagation: supplierFactorPropagationV1Schema,
  })
  .strict();

export const supplierFactorListCurrentRevisionV1Schema = supplierFactorRevisionV1Schema
  .pick({
    id: true,
    emissionsFactorId: true,
    revisionNumber: true,
    publicationState: true,
    emissions: true,
    authoritativeSefa: true,
    sourceSefaEvidence: true,
    indirectDetail: true,
    cbamFactor: true,
    reportingYear: true,
    origin: true,
    updatedAt: true,
  })
  .extend({ publicationState: z.literal(SupplierFactorPublicationState.Published) })
  .strict();

export const supplierFactorListItemV1Schema = z
  .object({
    historyAnchorRevisionId: supplierFactorObjectIdV1Schema,
    seriesKey: supplierFactorSeriesKeyV1Schema,
    interval: supplierFactorIntervalV1Schema,
    currentRevision: supplierFactorListCurrentRevisionV1Schema,
    propagation: supplierFactorPropagationV1Schema,
  })
  .strict();

// Fixpack #15 (D-15): the checklist's "edited a value" / "withdrew a value"
// steps need signals that don't survive in the published-only `items` list
// (a withdrawn series simply disappears from it). The backend is adding this
// top-level `summary` — OPTIONAL here so the FE parses cleanly against BOTH
// an old response (summary absent) and the new one, per the coordinated
// contract in FIXPACK-2026-07-23.md item 15.
export const supplierFactorListSummaryV1Schema = z
  .object({
    hasEdited: z.boolean(),
    hasWithdrawn: z.boolean(),
  })
  .strict();

export const supplierFactorListResponseV1Schema = z
  .object({
    version: z.literal(1),
    items: z.array(supplierFactorListItemV1Schema),
    summary: supplierFactorListSummaryV1Schema.optional(),
  })
  .strict();

export const supplierFactorHistoryResponseV1Schema = z
  .object({
    version: z.literal(1),
    historyAnchorRevisionId: supplierFactorObjectIdV1Schema,
    seriesKey: supplierFactorSeriesKeyV1Schema,
    revisions: z.array(supplierFactorRevisionV1Schema),
    propagationByRevisionId: z.record(
      supplierFactorObjectIdV1Schema,
      supplierFactorPropagationV1Schema,
    ),
  })
  .strict();

// Terminal propagation states carried on list/history/publish responses today.
// queued/processing only appear once the S10 drain worker exists, so the Sync
// badge must poll to advance past a terminal snapshot (see D-11).
export const SUPPLIER_FACTOR_PROPAGATION_TERMINAL_STATES = Object.freeze([
  SupplierFactorPropagationState.Complete,
  SupplierFactorPropagationState.Degraded,
]);

export const isSupplierFactorPropagationTerminal = (state) =>
  SUPPLIER_FACTOR_PROPAGATION_TERMINAL_STATES.includes(state);
